import { useMutation } from '@tanstack/react-query';
import { configApi } from '../../configApi';
import { IDataToRegistration } from '../CandidateArea/useGetDataToRegistration';
import { FieldValues } from 'react-hook-form';
import { IStatusProps } from '../SelectiveProcess/Types';
import { ISelectiveProcessVancancy } from '../SelectiveProcess/useRequestSelectiveProcessVacancy';
import { ETypeOfCompetition } from '../../../components/pages/Registration/DefaultVacancy';
import {
  getExemptionFileRoles,
  getExemptionType,
  isBoneMarrowExemption,
  isCadunicoExemption,
  isEconomicExemption,
} from '../Exemptions/economicHyposufficiency';
import { IGetRegistration } from './useGetRegistration';
import { preserveUnchangedCompetition } from './registrationCompetition';

interface ISpecialConditionList {
  id: string;
  observations: string;
}

interface IProtocolList {
  id: string;
  photo?: string;
  social_name?: string;
}

interface IMutationCreateRegistration {
  data: FieldValues;
  listTypeCompetition?: ISelectiveProcessVancancy[];
  selectiveProcess?: IDataToRegistration;
  registrationId?: string;
  oldData?: IGetRegistration | null;
}

interface IExemptionPayload {
  type?: string;
  nis?: string;
  document_number?: string;
  document_issuing_body?: string;
  document_issuing_body_uf?: string;
  mother_name?: string;
  redome_code?: string;
  is_declaration_veracity?: boolean;
  subtype?: string;
  file_descriptions?: string[];
  file_roles?: string[];
  remove_attachment_ids?: string[];
}

const requestCreateRegistration = async ({
  data,
  selectiveProcess,
  listTypeCompetition = [],
  registrationId,
  oldData,
}: IMutationCreateRegistration) => {
  const listToCreate = new FormData();
  const exemptionObject: IExemptionPayload = {};

  if (data?.type_exemption) {
    exemptionObject.is_declaration_veracity = data.is_declaration_veracity;
    const typeExemptionObject = getExemptionType(data.type_exemption);
    if (!typeExemptionObject) throw new Error('Selecione o tipo de isenção.');

    if (isEconomicExemption(typeExemptionObject.name)) {
      if (typeof data.subtype === 'string' && data.subtype.trim()) {
        exemptionObject.subtype = data.subtype;
      }
      exemptionObject.file_descriptions = (data.documents ?? []).map((document: { description: string }) =>
        document.description.trim(),
      );
      exemptionObject.file_roles = (data.documents ?? []).map((document: { role: string }) => document.role.trim());
      (data.documents ?? []).forEach((document: { file: File }) => {
        listToCreate.append('exemption_file', document.file);
      });
    }

    if (registrationId) {
      exemptionObject.remove_attachment_ids = data.removedExemptionAttachmentIds ?? [];
    }

    if (isCadunicoExemption(typeExemptionObject.name)) {
      if (data?.nis) {
        exemptionObject.nis = data.nis;
      }

      if (data?.mother_name) {
        exemptionObject.mother_name = data.mother_name;
      }

      if (data?.document_number) {
        exemptionObject.document_number = data.document_number;
      }

      if (data?.document_issuing_body) {
        exemptionObject.document_issuing_body = data.document_issuing_body;
      }

      if (data?.document_issuing_body_uf) {
        exemptionObject.document_issuing_body_uf = data.document_issuing_body_uf;
      }
    }

    if (isBoneMarrowExemption(typeExemptionObject.name)) {
      if (data?.redome_code) {
        exemptionObject.redome_code = data.redome_code;
      }
    }

    if (!isEconomicExemption(typeExemptionObject.name) && data?.attachmentExemption?.length > 0) {
      exemptionObject.file_roles = getExemptionFileRoles(typeExemptionObject.name, data.attachmentExemption);
      for (let index = 0; index < data.attachmentExemption.length; index++) {
        listToCreate.append('exemption_file', data.attachmentExemption[index]);
      }
    }

    exemptionObject.type = typeExemptionObject.id;
  }

  if (exemptionObject.type) {
    listToCreate.append('exemption', JSON.stringify(exemptionObject));
  }

  if (selectiveProcess?.allow_filter_vacancy_tree && data?.allow_filter_vacancy_tree) {
    listToCreate.append('allow_filter_vacancy_tree', data.allow_filter_vacancy_tree);
  }

  if (selectiveProcess?.allows_special_condition && data?.has_special_condition) {
    const isSpecialCondition = data.has_special_condition === 'yes';

    listToCreate.append('is_special_condition', `${isSpecialCondition}`);

    if (isSpecialCondition) {
      const specialConditionsIds: ISpecialConditionList[] = [];
      data.selectedSpecialCondition.forEach((value: boolean, index: number) => {
        if (value === false) return false;

        selectiveProcess?.special_condition.forEach((specialCondition) => {
          if (specialCondition.id === value.toString()) {
            const attach = data?.attachmentFile[index];
            if (attach && attach?.length > 0) {
              for (let index = 0; index < attach.length; index++) {
                listToCreate.append(`attachment_${value}`, attach[index]);
              }
            }
          }
        });

        specialConditionsIds.push({
          id: `${value}`,
          observations: data?.observation[index],
        });
      });
      listToCreate.append('special_conditions_ids', JSON.stringify(specialConditionsIds));
    }
  }

  const selectedProtocol: (string | boolean)[] = data.selectedProtocol;

  const isProtocol = selectedProtocol?.some((item) => typeof item === 'string' && item?.length > 0);

  if (selectedProtocol !== undefined) {
    listToCreate.set('is_protocol', String(Boolean(isProtocol)));
  }

  if (isProtocol) {
    const selectedProtocol: (string | boolean)[] = data.selectedProtocol;
    const isProtocol = selectedProtocol.some((item) => typeof item === 'string' && item.length > 0);

    listToCreate.set('is_protocol', `${isProtocol}`);

    if (isProtocol) {
      const protocolIds: IProtocolList[] = [];

      data.selectedProtocol.forEach((value: boolean, index: number) => {
        if (value === false) return false;

        selectiveProcess?.protocol.forEach((protocol) => {
          if (protocol.id === value.toString() && protocol.max_attached > 0) {
            const attach = data?.protocol_attachmentFile[index];
            if (attach && attach?.length > 0) {
              for (let index = 0; index < attach.length; index++) {
                listToCreate.append(`protocol_${value}`, attach[index]);
              }
            }
          }
        });

        selectiveProcess?.protocol.forEach((protocol) => {
          if (protocol.id === value.toString() && protocol.max_attached > 0) {
            if (data?.protocol_photo) {
              const attach = data?.protocol_photo[index];
              if (attach && attach?.length > 0) {
                for (let index = 0; index < attach.length; index++) {
                  listToCreate.append(`photo_${value}`, attach[index]);
                }
              }
            }
          }
        });

        protocolIds.push({
          id: `${value}`,
          // photo: `${value}`,
          social_name: data.social_name_protocol[index],
        });
      });

      if (protocolIds.length === 0) {
        listToCreate.delete('is_protocol');
        listToCreate.append('is_protocol', 'false');
      }
      listToCreate.append('protocols_ids', JSON.stringify(protocolIds));
    }
  }

  if (data.has_second_vacancy) {
    listToCreate.append('second_vacancy', data.initial_semester_secondary);
  } else {
    listToCreate.append('second_vacancy', '');
  }

  if (data.cid && data.deficiency && data.type_of_competition == ETypeOfCompetition.SPECIAL_VACANCY) {
    const existingPcd = oldData?.reserved_vacancy?.find((reserved) => reserved.type.toLowerCase().includes('pcd'));
    listToCreate.append(
      'reserved_vacancy_id',
      JSON.stringify({
        ...(existingPcd ? { id: existingPcd.id } : {}),
        cid: data.cid,
        deficiency: data.deficiency,
      }),
    );
  }

  // if (data.deficiency && data.type_of_competition == ETypeOfCompetition.SPECIAL_VACANCY) {
  //   listToCreate.append('deficiency', data.deficiency);
  // }

  if (data.vacancy) {
    listToCreate.append('selective_process_vacancy', data.vacancy);
  }

  // if (data.vagancy_type === 'is_initial_semester') {
  //   listToCreate.append('selective_process_vacancy', data.initial_semester);
  // }

  // if (data.vagancy_type === 'is_level_test') {
  //   listToCreate.append('selective_process_vacancy', data.nivel_test);
  // }

  if (
    data.modality_type &&
    data.modality_type.length > 0 &&
    data.type_of_competition == ETypeOfCompetition.SPECIAL_VACANCY
  ) {
    let isSubTypeCompetitionSelected = false;

    const modalityType = data.modality_type
      .filter((type: boolean) => type !== false)
      .map((value: string) => {
        const modality: IStatusProps = JSON.parse(value);
        if (modality?.name?.toLowerCase().includes('pcd')) {
          if (data?.medical_report && data?.medical_report?.length > 0) {
            for (let idxFile = 0; idxFile < data.medical_report.length; idxFile++) {
              listToCreate.append('medical_report_files', data.medical_report[idxFile]);
            }
          }
        }

        if (modality.name.toLowerCase().includes('pppiq')) {
          isSubTypeCompetitionSelected = true;
        }

        return modality.id;
      });

    listToCreate.append('type_of_competition', JSON.stringify(modalityType));
    listToCreate.append(
      'sub_types_of_competitions',
      JSON.stringify(
        isSubTypeCompetitionSelected
          ? (data.sub_type_of_competition ?? []).filter((item: string | boolean) => item)
          : [],
      ),
    );
  }

  if (
    data.is_all ||
    data.type_of_competition === ETypeOfCompetition.FULL_VACANCY ||
    data.type_of_competition === ETypeOfCompetition.UFC_SERVER_VACANCY
  ) {
    const selectedVacancy = listTypeCompetition.find((item) => item.id === data.vacancy);
    const normalizedSelectedType = String(data.type_of_competition).toLowerCase().replace(/[-_]/g, ' ');
    const competitionId =
      data.type_of_competition_id ||
      (data.type_of_competition === ETypeOfCompetition.FULL_VACANCY
        ? selectedVacancy?.type_of_competition.find((type) => type.name.toLowerCase().includes('ampla'))?.id
        : data.type_of_competition === ETypeOfCompetition.UFC_SERVER_VACANCY
          ? selectedVacancy?.type_of_competition.find((type) =>
              type.name.toLowerCase().replace(/[-_]/g, ' ').includes('ufc'),
            )?.id
          : selectedVacancy?.type_of_competition.find(
              (type) =>
                type.id === data.type_of_competition ||
                type.name.toLowerCase().replace(/[-_]/g, ' ') === normalizedSelectedType,
            )?.id || data.type_of_competition);

    if (competitionId) {
      listToCreate.set('type_of_competition', JSON.stringify([competitionId]));
      listToCreate.set('sub_types_of_competitions', JSON.stringify([]));
    }
  }

  if (selectiveProcess?.firearms_act == 'yes') {
    listToCreate.append('has_gun_license', `${data.weapon_carry_permit}`);
  }

  if (selectiveProcess?.juror_under_article_440) {
    const jurorArt = data.juror_art_440_cpp === 'yes';
    listToCreate.append('juror_art_440_cpp', `${jurorArt}`);
  }

  if (data.terms) {
    listToCreate.append('according_to_the_terms', data.terms);
  } else {
    listToCreate.append('according_to_the_terms', data.terms);
  }

  if (data.eligibility_declaration != undefined) {
    listToCreate.append('eligibility_declaration', data.eligibility_declaration);
  }

  if (registrationId) {
    if (oldData?.selective_process_vacancy?.id === data.vacancy) {
      listToCreate.delete('selective_process_vacancy');
    }
    preserveUnchangedCompetition(listToCreate, oldData?.type_of_competition);
    const response = await configApi.patch(`registration/update/${registrationId}/`, listToCreate);

    return response.data;
  }

  if (!registrationId) {
    const response = await configApi.post(`registration/create/`, listToCreate);

    return response.data;
  }
};

export const useCreateRegistration = () => {
  return useMutation({
    mutationKey: ['create_registration'],
    mutationFn: (payload: IMutationCreateRegistration) => requestCreateRegistration(payload),
  });
};
